// Split out of src/components/ChildView.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). ChildView.jsx
// re-exports everything it exported before, so existing imports keep working.

import { Celebration, CoinIcon, Icon, VaultCoinSparkAnimation, VaultIcon } from "../Common.jsx";
import { track } from "../../lib/metrics.js";
import { t as ct } from "../../lib/content.js";
import { useBackLayer } from "../../lib/backstack.js";
import { clampApr, daysToMoneyMonday, nextMondayPayoutPence, yearlyInterestPence } from "../../lib/vaultInterest.js";
import { isYoungChild } from "../../lib/age.js";
import { fmtNumber, fmtPence, tokensToPence } from "../../lib/utils.js";
import { CollapsibleSection } from "./kidShared.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// LB-385 (30 Sep): this is now the transfer BAR inside SavingsVaultCard rather than a card of
// its own. Its machinery is unchanged — the amount, the drag to the vault, the one pending
// transfer and its Cancel, the request path, focusKey/focusAmount — and only the chrome went:
// the card's own title and sub-line (the vault card says what the vault is, once) and the
// chevron header, whose job "Move tokens in" now does.
export function VaultTransferCard({ child, requests, onRequestTransfer, onCancelTransfer, rewardSettings, kidTheme, focusKey, focusAmount }) {
  const [expanded, setExpanded] = useState(false);
  const [amount, setAmount] = useState("");
  const [showPendingWarning, setShowPendingWarning] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [arrived, setArrived] = useState(false);
  // Full-screen "you did it!" overlay shown right after a successful send — this is the
  // savings-habit payoff moment, so it needs to be unmissable, not just the small in-card
  // coin confetti. null | { tokens, pence }
  const [transferOverlay, setTransferOverlay] = useState(null);
  useBackLayer(!!transferOverlay, () => setTransferOverlay(null)); // LB-315
  const trackRef = useRef(null);
  const dragStartClientX = useRef(0);
  const dragStartX = useRef(0);
  const COIN_W = 40;
  const rate = rewardSettings?.exchangeRate > 0 ? rewardSettings.exchangeRate : 10;
  const pendingTransfers = requests.filter((r) => r.requestType === "vault_transfer" && r.status === "pending");
  const hasPending = pendingTransfers.length > 0;
  const amt = parseInt(amount, 10) || 0;
  const pence = tokensToPence(amt, rate);
  const overBalance = amt > child.tokenBalance;
  const canDrag = amt > 0 && !overBalance && !hasPending;
  useEffect(() => {
    if (!arrived) return;
    const t = setTimeout(() => setArrived(false), 3200);
    // LB-385: a "nothing to send" empty state used to be returned from HERE, inside the
    // effect, so it never rendered (and React was handed JSX as a cleanup). It is gone; the
    // bar behaves exactly as it did, which is what the brief asks of it.
    return () => clearTimeout(t);
  }, [arrived]);

  // External "open me" signal (docs/VAULT_INTEREST_SCOPE.md): the Money Monday overlay's
  // "Add more" CTA and the grow-vault suggestion both land here — expand the bar and bring
  // it into view, so starting a move into the vault works from the new interest surfaces,
  // not just by scrolling to this card. Key 0 = initial mount, deliberately ignored.
  const cardRef = useRef(null);
  useEffect(() => {
    if (!focusKey) return;
    setExpanded(true);
    // A caller can pre-fill the amount along with the open signal (the grow-vault
    // suggestion's "Move X tokens" CTA) — clamped to what the child actually has, same
    // guard the free-typed input already applies via overBalance below.
    if (focusAmount) setAmount(String(Math.min(focusAmount, child.tokenBalance)));
    const t = setTimeout(() => { cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  function maxDrag() {
    return trackRef.current ? trackRef.current.offsetWidth - COIN_W : 0;
  }

  function handlePointerDown(e) {
    e.preventDefault();
    if (hasPending) { setShowPendingWarning(true); return; }
    if (!canDrag) return;
    setDragging(true);
    dragStartClientX.current = e.clientX;
    dragStartX.current = dragX;
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function handlePointerMove(e) {
    if (!dragging) return;
    const delta = e.clientX - dragStartClientX.current;
    setDragX(Math.max(0, Math.min(maxDrag(), dragStartX.current + delta)));
  }
  function handlePointerUp() {
    if (!dragging) return;
    setDragging(false);
    const track = maxDrag();
    if (track > 0 && dragX >= track - 20) {
      // Coin reached the vault — snap it fully in, fire the transfer, then reset.
      setDragX(track);
      const sent = onRequestTransfer(amt, pence);
      if (sent !== false) setTransferOverlay({ tokens: amt, pence });
      setAmount("");
      setShowPendingWarning(false);
      setArrived(true);
      setTimeout(() => setDragX(0), 700);
    } else {
      setDragX(0);
    }
  }

  // Per-child check restored (bug fix, Aug 2026): the "starts earning" line below must not be
  // shown to an opted-out child.
  const interestOn = !!rewardSettings?.vaultInterest?.enabled && child?.earnsVaultInterest !== false;

  return (
    <div ref={cardRef} className="relative mt-3" data-testid="vault-transfer-bar">
      {arrived && <Celebration variant="coins" count={16} />}
      {/* LB-237 (Alan, 8 Sep): the "pending" chip on its own left a child guessing. */}
      {hasPending && <p className="text-sm text-amber-700 mb-1 flex items-center gap-1.5"><span className="text-xs font-semibold text-amber-600 bg-amber-50 rounded px-2 py-0.5"><Icon id="ic-pending" size={12} className="inline-block align-[-2px] mr-1" style={{ color: "currentColor" }} />{ct("kid.pending_chip", "pending")}</span>{ct("vault.pending_explainer", "Waiting for a grown-up to confirm your transfer")}</p>}
      {!expanded && (
        <>
          <div className="relative h-10 mb-0.5 overflow-hidden">
            {/* A 4px rounded track in the teal tint — on the white sheet a 1px grey line read as a
                hairline, not a slider (Alan, 10 Sep). */}
            <div className="absolute top-1/2 left-9 right-9 h-1 -mt-0.5 rounded-full" style={{ background: "var(--fam-tint, #D3F0EC)" }} />
            <CoinIcon className="w-8 h-8 absolute top-1 left-0" />
            <VaultIcon className="w-8 h-8 absolute top-1 right-1" />
          </div>
          <div className="flex items-center justify-between gap-2 mt-1">
            <span className="text-xs text-slate-500 min-w-0">{ct("kid.vault.could_move", "{n} tokens you could move", { n: fmtNumber(Math.max(0, child.tokenBalance || 0)) })}</span>
            <button type="button" className="bg-slate-900 text-white text-sm font-bold rounded-lg px-3 py-1.5 flex-shrink-0" onClick={() => setExpanded(true)} data-testid="vault-move-in">{ct("kid.vault.move_in", "Move tokens in")}</button>
          </div>
        </>
      )}
      {/* (Alan, Aug 2026) The "Ask your parent to turn on your Savings Vault!" branch that
          used to sit opposite this one is gone — the vault can't be switched off any more,
          so that empty state was unreachable. */}
      {expanded && (
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-1">
            <label className="text-xs font-medium text-slate-500">{ct("vault.how_many", "How many tokens do you want to save?")}</label>
            <input className="border border-slate-200 rounded-lg px-2.5 py-2 text-sm w-20 flex-shrink-0" placeholder="Tokens" type="number" min="1" max={child.tokenBalance} value={amount} onChange={(e) => { setAmount(e.target.value.replace(/\D/g, "")); setShowPendingWarning(false); setDragX(0); }} />
            {amt > 0 && <span className="text-xs text-slate-500 font-medium whitespace-nowrap flex-shrink-0">≈ {fmtPence(pence)}</span>}
          </div>
          {interestOn && amt > 0 && !overBalance && (
            <p className="text-xs font-semibold text-teal-600 mb-1">{ct("vault.transfer_earning_line", "…and it starts earning from next Money Monday.")}</p>
          )}
          {canDrag && (
            <p className="text-base text-slate-700 font-semibold mb-2">{ct("vault.drag_hint", "Now drag the tokens to the Savings Vault.")}</p>
          )}
          <div ref={trackRef} className="relative h-12 mb-1 overflow-hidden touch-none">
            {/* A 4px rounded track in the teal tint — on the white sheet a 1px grey line read as a
              hairline, not a slider (Alan, 10 Sep). */}
          <div className="absolute top-1/2 left-9 right-9 h-1 -mt-0.5 rounded-full" style={{ background: "var(--fam-tint, #D3F0EC)" }} />
            <span
              className={`absolute top-1 ${dragging ? "" : "transition-all ease-out"}`}
              style={{ left: `${dragX}px`, touchAction: "none", cursor: canDrag ? "grab" : "default", opacity: canDrag ? 1 : 0.4, userSelect: "none" }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onDragStart={(e) => e.preventDefault()}
            >
              <CoinIcon className="w-10 h-10" />
            </span>
            <VaultIcon className="w-10 h-10 absolute top-0 right-1" />
          </div>
          {!canDrag && !hasPending && (
            <p className="text-base text-slate-400 text-center mb-1">{ct("vault.enter_then_drag", "Enter how many tokens to move, then drag the coin.")}</p>
          )}
          {amt > 0 && overBalance && (
            <div className="text-xs mt-2 text-rose-500 font-semibold">You only have {child.tokenBalance} tokens</div>
          )}
          {showPendingWarning && hasPending && (
            <p className="text-base text-amber-600 font-semibold mt-2">{ct("vault.transfer_waiting", "You already have a transfer waiting on a parent — cancel it below to send a new one.")}</p>
          )}
          {/* The chevron that used to fold the card is gone with its header; this does its job. */}
          <button type="button" className="text-sm font-bold text-slate-500 mt-1" onClick={() => { setExpanded(false); setAmount(""); setDragX(0); setShowPendingWarning(false); }} data-testid="vault-move-close">{ct("common.cancel", "Cancel")}</button>
        </div>
      )}
      {expanded && pendingTransfers.map((r) => (
        <div key={r.id} className="flex items-center justify-between mt-2 text-xs border-t border-slate-100 pt-2 gap-2">
          <span className="text-slate-500 flex-1">Move {r.requestedAmount} tokens (≈{fmtPence(r.pence)})</span>
          <span className="font-bold px-2 py-0.5 rounded flex-shrink-0 bg-amber-50 text-amber-600"><Icon id="ic-pending" size={12} className="inline-block align-[-2px] mr-1" style={{ color: "currentColor" }} />{ct("kid.pending_chip", "pending")}</span>
          <button className="text-xs font-bold text-rose-500 flex-shrink-0" onClick={() => onCancelTransfer(r.id)}>{ct("kid.cancel_button", "Cancel")}</button>
        </div>
      ))}
      {transferOverlay && <VaultTransferOverlay tokens={transferOverlay.tokens} pence={transferOverlay.pence} onDone={() => setTransferOverlay(null)} />}
    </div>
  );
}

// LB-385 (Alan, 30 Sep): ONE "My Savings Vault" card on Rewards, in place of the transfer card
// and VaultGrowthCard, which told the 5% and Money Monday in almost the same sentence. Top to
// bottom: the balance (with "Money Monday in n days" when interest is on for this child), one
// line saying what the vault is, the transfer bar (VaultTransferCard, unchanged in behaviour),
// and, only when interest is on, two tiles — next Money Monday, and a year from now if it is
// left — with "How does it grow?" opening the explainer. Interest on/off is VaultGrowthCard's
// own test (per child: a child a parent opted out sees no interest surface at all). The card's
// lines are one register for every age; the under-8 split lives inside the explainer only.
export function SavingsVaultCard({ child, requests, onRequestTransfer, onCancelTransfer, rewardSettings, kidTheme, focusKey, focusAmount }) {
  const [explainerOpen, setExplainerOpen] = useState(false);
  useBackLayer(explainerOpen, () => setExplainerOpen(false)); // LB-315
  const vi = rewardSettings?.vaultInterest;
  const interestOn = !!vi?.enabled && child?.earnsVaultInterest !== false;
  const apr = clampApr(vi?.apr);
  const balance = child.vaultBalancePence || 0;
  const days = interestOn ? daysToMoneyMonday(child) : null;
  const chip = days === 0 ? ct("kid.vault.mm_today", "Money Monday today")
    : days === 1 ? ct("kid.vault.mm_tomorrow", "Money Monday tomorrow")
    : ct("kid.vault.mm_in", "Money Monday in {n} days", { n: days });
  return (
    <CollapsibleSection title={ct("kid.rewards.section_vault", "My Savings Vault")} tone="saving" icon={<VaultIcon className="w-4 h-4 inline-block" />} variant="section">
      <div className="bg-white border border-slate-100 rounded-xl p-3" data-testid="savings-vault-card" data-interest={interestOn ? "on" : "off"}>
        {/* flex-wrap (LB-385 report): at 360px the chip beside a £ balance has no room, and drops
            under it rather than running off the card. At 390px it sits beside it, as drawn. */}
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <span className="text-2xl font-extrabold text-slate-900" data-testid="vault-balance">{fmtPence(balance)}</span>
          {interestOn && <span className="text-xs font-bold rounded-full px-2.5 py-1 bg-teal-50 text-teal-800 whitespace-nowrap" data-testid="vault-mm-chip">{chip}</span>}
        </div>
        <div className="text-sm text-slate-600 mt-1" data-testid="vault-line">{interestOn
          ? ct("kid.vault.line_on", "Money you're keeping safe. Every Money Monday it grows.")
          : ct("kid.vault.line_off", "Money you're keeping safe \u2013 not for spending by accident.")}</div>
        <VaultTransferCard child={child} requests={requests} onRequestTransfer={onRequestTransfer} onCancelTransfer={onCancelTransfer} rewardSettings={rewardSettings} kidTheme={kidTheme} focusKey={focusKey} focusAmount={focusAmount} />
        {interestOn && (
          <>
            <div className="grid grid-cols-2 gap-2 mt-3" data-testid="vault-tiles">
              <div className="rounded-lg bg-teal-50 px-3 py-2 min-w-0">
                <div className="text-[11px] font-bold text-teal-800">{ct("kid.vault.tile_next", "Next Money Monday")}</div>
                <div className="text-base font-extrabold text-emerald-700">+{fmtPence(nextMondayPayoutPence(child, apr))}</div>
              </div>
              <div className="rounded-lg bg-teal-50 px-3 py-2 min-w-0">
                <div className="text-[11px] font-bold text-teal-800">{ct("kid.vault.tile_year", "In a year, if you leave it")}</div>
                <div className="text-base font-extrabold text-teal-900">{fmtPence(balance + yearlyInterestPence(balance, apr))}</div>
              </div>
            </div>
            <button type="button" className="mt-2 text-sm font-bold text-teal-800 flex items-center gap-1" onClick={() => setExplainerOpen(true)} data-testid="vault-how"><span>{ct("kid.vault.how", "How does it grow?")}</span><Icon id="ic-forward" size={14} style={{ color: "currentColor" }} /></button>
          </>
        )}
      </div>
      {explainerOpen && <VaultGrowthExplainer child={child} apr={apr} yearly={yearlyInterestPence(balance, apr)} young={isYoungChild(child)} onClose={() => setExplainerOpen(false)} />}
    </CollapsibleSection>
  );
}

// The "How does it work?" sheet — same fixed-overlay pattern as VaultTransferOverlay.
// The accelerating bars ARE the compound-interest lesson: £10 at the household's rate
// across a year, computed live so a rate change keeps the chart honest.
function VaultGrowthExplainer({ child, apr, yearly, young, onClose }) {
  const points = [0, 1, 2, 3, 4].map((years) => Math.round(1000 * Math.pow(1 + apr / 100 / 52, 52 * years)));
  const max = points[points.length - 1];
  // Backdrop is NOT a dismiss target (Alan, 28 Aug): closing happens only via the
  // explicit ✕ below. A stray tap beside a sheet used to drop the whole thing.
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-xs shadow-2xl overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between bg-slate-900">
          <span className="text-sm font-bold text-white flex items-center gap-1.5"><Icon id="ic-supersaver" size={16} hue="knockout" />{ct("vault.explainer_title", "How your vault grows")}</span>
          <button className="text-white/90 font-bold w-5 h-5 flex items-center justify-center" onClick={onClose} aria-label="Close"><Icon id="ic-close" size={16} style={{ color: "currentColor" }} /></button>
        </div>
        <div className="p-4">
          {young ? (
            <div className="text-base text-slate-600 space-y-2">
              <p className="flex items-start gap-1"><VaultIcon className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{ct("vault.explainer_young_1", "Every Money Monday, the coins in your vault make baby coins.")}</span></p>
              <p>{ct("vault.explainer_young_2", "Next week, the baby coins make babies too.")}</p>
              <p>{ct("vault.explainer_young_3", "So the longer your coins stay in, the faster your pile grows.")}</p>
            </div>
          ) : (
            <div className="text-base text-slate-600 space-y-2">
              <p>{ct("vault.explainer_older_1", "Interest is money your vault pays you just for saving — {apr}% a year, in weekly Money Monday instalments.", { apr })}</p>
              <p>{ct("vault.explainer_older_2", "The clever bit: each payment goes INTO the vault, so next Monday you earn interest on your interest. That's compound interest — it starts slow and speeds up, which is why starting early wins.")}</p>
            </div>
          )}
          <div className="mt-3">
            <div className="text-xs font-medium text-slate-500 mb-1.5">{ct("vault.explainer_chart_label", "£10 left in the vault at {apr}% a year:", { apr })}</div>
            <div className="flex items-end gap-1.5 h-20">
              {points.map((v, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <div className="w-full rounded-t-md bg-teal-400" style={{ height: `${Math.max(8, Math.round((v / max) * 100))}%`, opacity: 0.55 + i * 0.11 }} />
                  <div className="text-xs text-slate-500 font-medium whitespace-nowrap">{i === 0 ? ct("vault.explainer_chart_now", "now") : ct("vault.explainer_chart_year", "{n}yr", { n: i })}<br />{fmtPence(v)}</div>
                </div>
              ))}
            </div>
          </div>
          {!young && yearly > 0 && (
            <p className="text-xs text-slate-400 mt-2.5">{ct("vault.explainer_yearly_line", "Right now your {balance} vault earns about {yearly} a year — for doing nothing.", { balance: fmtPence(child.vaultBalancePence), yearly: fmtPence(yearly) })}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Money Monday — the interest-payment celebration (docs/VAULT_INTEREST_SCOPE.md). Same
// unmissable fixed-overlay treatment as VaultTransferOverlay, same coin Celebration as
// every other money moment (Law: money celebrations always keep coin imagery — see
// partyEmoji's comment in ChildView), and the one line that carries the whole feature's
// lesson at the moment of maximum delight: your money worked while you slept.
// "Add more to my vault" strikes while the excitement is hot — it jumps to Rewards and
// opens the transfer card (still parent-approved, so it's a safe impulse to invite).
export function MoneyMondayOverlay({ child, pence, earnedPence, canAddMore, onAddMore, onDone }) {
  const young = isYoungChild(child);
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-xs shadow-2xl text-center relative overflow-hidden">
        <Celebration variant="coins" count={22} />
        <div className="relative flex items-center justify-center gap-1 mb-2"><VaultIcon className="w-14 h-14" /><Icon id="ic-supersaver" size={44} hue="teal" /></div>
        <p className="relative text-lg font-extrabold text-slate-800">{ct("vault.mm_title", "It's Money Monday")}</p>
        <p className="relative text-sm text-slate-600 mt-1">{young ? ct("vault.mm_sub_young", "Your vault made baby coins.") : ct("vault.mm_sub_older", "Your savings just earned")}</p>
        <p className="relative text-3xl font-extrabold text-teal-600 my-1.5">+{fmtPence(pence)}</p>
        <p className="relative text-xs text-slate-500">{young
          ? ct("vault.mm_line_young", "They stayed in your vault… and grew.")
          : ct("vault.mm_line_older", "You didn't lift a finger — your money worked for you while you slept.")}</p>
        {earnedPence > 0 && (
          <div className="relative rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 mt-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-amber-800">{ct("vault.growth_earned_label", "Earned so far")}</span>
            <span className="text-xs font-bold text-amber-800">{fmtPence(earnedPence)}</span>
          </div>
        )}
        <button className="relative w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mt-4" onClick={onDone}>{ct("vault.mm_dismiss", "Nice")}</button>
        {canAddMore && (
          <button className="relative w-full text-teal-600 font-bold text-sm py-2 mt-1" onClick={onAddMore}>{ct("vault.mm_add_more", "Add more to my vault")}</button>
        )}
      </div>
    </div>
  );
}

// Full-screen overlay shown the instant a child's Savings Vault transfer lands — their balance
// has already moved for real (see submitVaultTransfer in App.jsx). Phase 1 reuses the exact
// same coin-spark-around-the-vault animation as the parent-side VaultDrainModal (shown after a
// parent approves something touching the vault), so the same underlying event reads the same
// way on both sides of the app. Phase 2 is the celebration + explicit "a parent still needs to
// confirm this" note, since the real money hasn't actually moved between bank accounts yet
// even though the app's own balances have. "Got it!" is the only way out, so a stray tap can't
// dismiss it before it's actually read.
export function VaultTransferOverlay({ tokens, pence, onDone }) {
  const [phase, setPhase] = useState("anim");
  useEffect(() => {
    if (phase !== "anim") return;
    const t = setTimeout(() => setPhase("celebrate"), 1800);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-xs shadow-2xl text-center relative overflow-hidden">
        {phase === "anim" ? (
          <>
            <VaultCoinSparkAnimation />
            <p className="text-sm font-semibold text-slate-700">Sending {tokens} tokens (≈{fmtPence(pence)}) to your Savings Vault…</p>
          </>
        ) : (
          <>
            <Celebration variant="coins" count={20} />
            <div className="relative flex items-center justify-center gap-2 mb-3">
              <CoinIcon className="w-12 h-12" />
              <span className="text-2xl text-slate-300">→</span>
              <VaultIcon className="w-12 h-12" />
            </div>
            <p className="relative text-base font-semibold text-slate-800 mb-1">{ct("kid.nice_one", "Nice one")}</p>
            <p className="relative text-sm text-slate-600 mb-1">
              You've sent <span className="font-bold text-slate-800">{tokens} tokens</span> (≈{fmtPence(pence)}) to your Savings Vault.
            </p>
            <p className="relative text-xs text-slate-400 mb-4">We'll get your parent to confirm the transfer.</p>
            <button className="relative w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5" onClick={onDone}>{ct("kid.got_it", "Got it")}</button>
          </>
        )}
      </div>
    </div>
  );
}

// Grow-vault suggestion (docs/VAULT_INTEREST_SCOPE.md): a proactive nudge for idle tokens
// while interest is running — "keeping balances accurate saves money" cuts both ways, and
// this is the encouraging half aimed at the kid rather than the parent's stale-vault note.
// Suggests moving 20% of the token balance (leaves plenty spendable, still meaningfully
// grows the vault) and states the concrete weekly payoff so it's a real number, not a
// vague "you should save more". Same fixed-overlay pattern as every other money moment.
export function VaultGrowSuggestOverlay({ child, moveTokens, movePence, weeklyPence, onMove, onDismiss }) {
  const young = isYoungChild(child);
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-xs shadow-2xl text-center relative overflow-hidden">
        <div className="flex items-center justify-center gap-2 mb-2"><CoinIcon className="w-10 h-10" /><span className="text-xl text-slate-300">→</span><VaultIcon className="w-10 h-10" /></div>
        <p className="text-lg font-extrabold text-slate-800">{ct("vault.suggest_title", "Got spare tokens?")}</p>
        <p className="text-sm text-slate-600 mt-2">
          {young
            ? ct("vault.suggest_sub_young", "Move {tokens} tokens to your vault and leave them there — they'll make baby coins.", { tokens: moveTokens })
            : ct("vault.suggest_sub_older", "Move {tokens} tokens (≈{pence}) to your vault and leave it there…", { tokens: moveTokens, pence: fmtPence(movePence) })}
        </p>
        <div className="rounded-xl bg-teal-50 border border-teal-100 px-3 py-2.5 mt-3">
          <p className="text-sm font-bold text-teal-800">
            {young
              ? ct("vault.suggest_result_young", "…and make baby coins every Money Monday.")
              : ct("vault.suggest_result_older", "…and make {amount} interest every Money Monday.", { amount: fmtPence(weeklyPence) })}
          </p>
        </div>
        <button className="w-full mt-4 bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 flex items-center justify-center gap-1.5" onClick={onMove}><CoinIcon className="w-4 h-4" /> {ct("vault.suggest_move_cta", "Move {tokens} tokens", { tokens: moveTokens })}</button>
        <button className="w-full text-slate-400 font-semibold text-sm py-2 mt-1" onClick={onDismiss}>{ct("vault.suggest_dismiss", "Maybe later")}</button>
      </div>
    </div>
  );
}
